import { computed, toValue } from 'vue'
// types
import type { ComputedRef, MaybeRefOrGetter, Ref } from 'vue'
import type { WpArticle, WpAuthor, WpFeaturedMedia, WpFeaturedMediaSize, WpPost } from '@ts-wp/interfaces'
// helpers
import dayjs from 'dayjs'

interface WpArticlesHookReturn {
  refArticles: ComputedRef<WpArticle[]>
  refPending: Ref<boolean>
  refError: Ref<Error | null>
}

// Strip HTML tags + decode common entities. WP `excerpt.rendered` returns
// `<p>...</p>` and `title.rendered` may contain `&amp;`, `&#8217;`, etc.
const decodeHtmlEntities = (raw: string): string => {
  return raw
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, '\'')
    .replace(/&#8217;/g, '’')
    .replace(/&#8216;/g, '‘')
    .replace(/&#8220;/g, '“')
    .replace(/&#8221;/g, '”')
    .replace(/&#8211;/g, '–')
    .replace(/&#8212;/g, '—')
    .replace(/&hellip;/g, '…')
    .replace(/&nbsp;/g, ' ')
}

const stripTags = (raw: string): string => raw.replace(/<[^>]+>/g, '')

// Pick a reasonable size for a 180px-tall card. WP returns many named sizes
// (`thumbnail`, `medium`, `medium_large`, `large`, `full`, plus theme-specific
// like `blog-thumbnail`, `blog-main-post`). Try theme-specific first, then
// medium_large, then fall back to the raw `source_url`.
interface WpRendition {
  url: string
  width: number | null
  height: number | null
}

const toRendition = (size: WpFeaturedMediaSize | undefined): WpRendition | null => (
  size?.source_url ? { url: size.source_url, width: size.width ?? null, height: size.height ?? null } : null
)

const originalRendition = (media: WpFeaturedMedia): WpRendition | null => (
  media.source_url
    ? { url: media.source_url, width: media.media_details?.width ?? null, height: media.media_details?.height ?? null }
    : null
)

const pickCardImage = (media: WpFeaturedMedia | undefined): WpRendition | null => {
  if (!media) return null
  const sizes = media.media_details?.sizes
  return (
    toRendition(sizes?.['blog-thumbnail'])
    ?? toRendition(sizes?.['medium_large'])
    ?? toRendition(sizes?.medium)
    ?? originalRendition(media)
  )
}

// A bigger rendition for wide covers (BlogCard renders up to 432px CSS width,
// ~864px on retina): large (1024) first, then medium_large (768), then the original.
const pickCoverImage = (media: WpFeaturedMedia | undefined): WpRendition | null => {
  if (!media) return null
  const sizes = media.media_details?.sizes
  return (
    toRendition(sizes?.large)
    ?? toRendition(sizes?.['medium_large'])
    ?? originalRendition(media)
  )
}

const AVATAR_SIZES = ['48', '96'] as const

const pickAvatar = (author: WpAuthor | undefined): { url: string, size: number } | null => {
  for (const size of AVATAR_SIZES) {
    const url = author?.avatar_urls?.[size]
    if (url) return { url, size: Number(size) }
  }
  return null
}

const normaliseWpPost = (post: WpPost): WpArticle => {
  const featured = post._embedded?.['wp:featuredmedia']?.[0]
  const author = post._embedded?.author?.[0]
  // First inner array of wp:term is categories. Take the first category.
  const category = post._embedded?.['wp:term']?.[0]?.[0] ?? null
  const card = pickCardImage(featured)
  const cover = pickCoverImage(featured)
  const avatar = pickAvatar(author)

  return {
    id: post.id,
    link: post.link,
    title: decodeHtmlEntities(post.title?.rendered ?? '').trim(),
    excerpt: decodeHtmlEntities(stripTags(post.excerpt?.rendered ?? '')).trim(),
    date: post.date,
    dateFormatted: post.date ? dayjs(post.date).format('MMM D, YYYY') : '',
    category: category?.name ? decodeHtmlEntities(category.name) : null,
    image: card?.url ?? null,
    imageWidth: card?.width ?? null,
    imageHeight: card?.height ?? null,
    imageLarge: cover?.url ?? null,
    imageLargeWidth: cover?.width ?? null,
    imageLargeHeight: cover?.height ?? null,
    imageAlt: featured?.alt_text || decodeHtmlEntities(post.title?.rendered ?? ''),
    authorName: author?.name ? decodeHtmlEntities(author.name) : null,
    authorAvatar: avatar?.url ?? null,
    authorAvatarSize: avatar?.size ?? null,
  }
}

// Fetch WP REST API posts at SSR time. The URL comes from Strapi's `relationLink`
// field (typically `https://brighterly.com/wp-json/wp/v2/posts?per_page=3&_embed=1`).
// useAsyncData caches by URL so the same endpoint isn't re-fetched per nav.
export const useWpArticles = async (url: MaybeRefOrGetter<string>): Promise<WpArticlesHookReturn> => {
  const refUrl = computed<string>(() => toValue(url))

  const { data, pending, error } = await useAsyncData<WpArticle[]>(
    `wp:articles:${refUrl.value}`,
    async () => (await $fetch<WpPost[]>(refUrl.value)).map(normaliseWpPost),
    { watch: [refUrl], default: () => [] },
  )

  const refArticles = computed<WpArticle[]>(() => data.value ?? [])

  return {
    refArticles,
    refPending: pending,
    refError: error as Ref<Error | null>,
  }
}